import type { CollectionSummaryJSONObject, TagJSONObject, TagSummaryJSONObject } from '@ikegps/core-api';
import { action, computed, makeObservable } from 'mobx';
import type { SelectAutoCompleteOption } from '../../components/autocomplete.menu/autocomplete.menu';
import { InputState } from '../../components/input/input.state';
import { type ModalResponse, ModalService } from '../../components/modal/modal.service';
import { T, Translate } from '../../i18n';
import { TagEditState } from '../../page/tag.edit.state';
import { TagState } from '../../page/tag.state';
import { Logger, Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { TagNameInputConfirmationContent } from './tag.name.input.confirmation';

export class TagNameInputController {
	private static CAN_CANCEL_RENAME: boolean = true;

	tag: TagJSONObject;

	private tagState: TagState;
	private editState: TagEditState;
	private _tagCard?: HTMLElement;

	constructor(tagState: TagState, tag: TagJSONObject) {
		makeObservable(this);

		this.tagState = tagState;
		this.editState = tagState.edit;
		this.tag = tag;
	}

	getInputState(): InputState<string> {
		return this.editState.input;
	}

	getInputValue(): string {
		return this.editState.input.value;
	}

	getTagFilterValue(): string {
		return Services.Search.filterState.filter;
	}

	zoomToTag() {
		this.tagState.map.zoomToTag(this.tag.objectId);

		if (CurrentState.expandedTagId === this.tag.objectId) {
			this.tagState.collapseTag(this.tag);
		} else {
			this.tagState.expandTag(this.tag);
		}
	}

	@action updateEdit(value: string) {
		this.editState.input.setTouched();
		this.editState.input.setValue(value);
	}

	async acceptEdit(): Promise<void> {
		const name = this.editState.input.value;

		if (name == null) {
			this.cancelEdit();
			return Promise.resolve();
		}

		const cleanName = name.trim();
		if (cleanName.length === 0 || cleanName === this.tag.name) {
			this.cancelEdit();
			return Promise.resolve();
		}

		const tagSummary = AppStore.TagSummary.get(this.tag.objectId);
		if (tagSummary == null) {
			Logger.error(`Cannot rename tag '${this.tag.name}' before tag summary retrieved.`);
			return Promise.resolve();
		}

		// Make sure this tag is not selected if we are renaming it.
		CurrentState.Tag.selectTag(this.tag.objectId, false);

		const isMerge = this.tagState.tags.some((t) => t.name === name);
		const modelResponse = await this.showRenameModal(isMerge, tagSummary.revisions.length, name);
		this.cancelEdit();

		if (modelResponse.cancel) {
			return;
		}

		this.editState.setRenaming(this.tag.objectId);

		if (isMerge) {
			const collections = await this.getCollections(tagSummary);
			await Services.Collection.move(collections, name);
		} else {
			await Services.Tag.rename(this.tag.objectId, name);

			// this.tag is now outdated, we need to reassign it to the updated tag
			const updatedTag = AppStore.Tag.get(this.tag.objectId);
			if (updatedTag && updatedTag !== this.tag) {
				this.tag = updatedTag;
			}
		}

		CurrentState.clearSelected();
		this.editState.cancelRenaming();
	}

	@action cancelEdit() {
		this.editState.setTagId(null);
		this.editState.input.reset();

		if (this._tagCard) {
			Services.FocusAway.dropByElement(this._tagCard);
		}
	}

	@computed get autoCompleteOptions(): SelectAutoCompleteOption[] {
		return this.tagState.tags
			.filter((tag) => tag.objectId !== this.tag.objectId)
			.map((tag) => ({ id: tag.objectId, value: tag.name }));
	}

	set tagCard(tagCard: HTMLElement) {
		this._tagCard = tagCard;
	}

	private showRenameModal(merge: boolean, collectionCount: number, newName: string): Promise<ModalResponse> {
		if (merge) {
			return ModalService.open({
				title: Translate(T.TAG_MERGE_TITLE),
				content: TagNameInputConfirmationContent.view(collectionCount, this.tag.name, newName),
				hasCancel: TagNameInputController.CAN_CANCEL_RENAME,
			});
		}

		return Promise.resolve({ ok: true, cancel: false });
	}

	private getCollections(tagSummary: TagSummaryJSONObject): Promise<CollectionSummaryJSONObject[]> {
		const collections: Promise<CollectionSummaryJSONObject>[] = [];

		for (const revision of tagSummary.revisions) {
			const collectionSummary = AppStore.CollectionSummary.get(revision);
			if (collectionSummary != null) {
				collections.push(Promise.resolve(collectionSummary));
			} else {
				collections.push(AppStore.CollectionSummary.fetch(revision));
			}
		}

		return Promise.all(collections);
	}
}
